import { keyframes, styled } from "@pigment-css/react";
import * as Dialog from "@radix-ui/react-dialog";
import { CompleteIcon } from "@acto/web-common/components";
import { useBodyScrollLock } from "@acto/taskpane/field-sales-2/components/primitives/useBodyScrollLock";

const fadeScaleIn = keyframes({
  from: { opacity: 0, transform: "scale(0.96)" },
  to: { opacity: 1, transform: "scale(1)" },
});

const fadeOut = keyframes({ from: { opacity: 1 }, to: { opacity: 0 } });

const Surface = styled(Dialog.Content)(({ theme }) => ({
  position: "fixed",
  inset: 0,
  zIndex: 1300,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "2.5rem",
  paddingBottom: "6rem",
  cursor: "pointer",
  outline: "none",
  background: theme.fieldSalesTwo.colors.background,
  willChange: "transform, opacity",
  '&[data-state="open"]': {
    animation: `${fadeScaleIn} ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized}`,
  },
  '&[data-state="closed"]': {
    animation: `${fadeOut} ${theme.fieldSalesTwo.motion.duration.sheetOut} ${theme.fieldSalesTwo.motion.easing.standard}`,
    pointerEvents: "none",
  },
  "@media (prefers-reduced-motion: reduce)": {
    '&[data-state="open"]': {
      animation: `${fadeScaleIn} ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
    },
    '&[data-state="closed"]': {
      animation: `${fadeOut} ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
    },
  },
}));

const Container = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "1.75rem",
});

const Icon = styled("span")(({ theme }) => ({
  display: "flex",
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const Text = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  maxWidth: "20rem",
  textAlign: "center",
  wordBreak: "break-word",
}));

const Title = styled(Dialog.Title)(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[22],
  fontWeight: theme.fieldSalesTwo.font.weight[600],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const Subtitle = styled(Dialog.Description)(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[17],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const Hint = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[13],
  fontWeight: theme.fieldSalesTwo.font.weight[400],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[700],
  textAlign: "center",
}));

interface FullScreenSuccessModalProps {
  open: boolean;
  title: string;
  subtitle: string;
  /** Muted footer hint, e.g. "Antippen zum Fortfahren". */
  hint: string;
  /** Fired when the overlay is tapped or dismissed via Escape. */
  onDismiss: () => void;
}

/**
 * Full-screen success confirmation. Presentational only — visibility is owned by
 * SuccessModalProvider. Tapping anywhere (or Escape) dismisses it.
 */
export function FullScreenSuccessModal({ open, title, subtitle, hint, onDismiss }: FullScreenSuccessModalProps) {
  useBodyScrollLock(open);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
    >
      <Dialog.Portal>
        <Surface onClick={onDismiss}>
          <Container>
            <Icon>
              <CompleteIcon width={96} height={96} aria-hidden="true" />
            </Icon>
            <Text>
              <Title>{title}</Title>
              <Subtitle>{subtitle}</Subtitle>
            </Text>
          </Container>
          <Hint>{hint}</Hint>
        </Surface>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
